import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { authenticatedFetch } from '../../utils/api';
import { 
  DollarSign, 
  FileText, 
  TrendingUp,
  Calendar,
  RefreshCw,
  BookOpen,
  Gavel,
  Building2,
  Users,
  Activity
} from 'lucide-react';

const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchOverviewStats();
  }, []);

  const fetchOverviewStats = async () => {
    try {
      setLoading(true);
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await authenticatedFetch(`${API_BASE}/api/admin/dashboard-overview-stats?t=${timestamp}`, {
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Fresh dashboard stats:', data);
        setStats(data);
      } else {
        throw new Error('Failed to fetch overview statistics');
      }
    } catch (error) {
      console.error('Error fetching overview stats:', error);
      toast({
        title: "Error",
        description: "Failed to load overview statistics",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-gray-200 rounded animate-pulse mb-2"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse w-20"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-gray-500">No statistics available</p>
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (amount) => {
    // Format currency with Ghana Cedis (GH₵)
    return `GH₵${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)}`;
  };

  return (
    <div className="space-y-6">
      {/* Header with refresh button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
          <p className="text-gray-600">Key metrics and statistics</p>
        </div>
        <Button variant="outline" onClick={fetchOverviewStats} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Total Revenue */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats.total_revenue_cumulative || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Cumulative earnings
            </p>
          </CardContent>
        </Card>

        {/* This Month Revenue */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats.total_revenue_this_month || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Monthly revenue
            </p>
          </CardContent>
        </Card>

        {/* Active Users */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active_users || 0}</div>
            <p className="text-xs text-muted-foreground">
              Registered users
            </p>
          </CardContent>
        </Card>

        {/* Active Subscriptions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Subscriptions</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active_subscriptions || 0}</div>
            <p className="text-xs text-muted-foreground">
              Active subscriptions
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Lesson Notes Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Lesson Notes Statistics
          </CardTitle>
          <CardDescription>
            Revenue tracking and submission analytics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-600">This Term Revenue</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(stats.lesson_notes_stats?.revenue_this_term || 0)}
              </p>
              <p className="text-xs text-gray-500">
                {stats.lesson_notes_stats?.this_term_submissions || 0} submissions
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(stats.lesson_notes_stats?.revenue_cumulative || 0)}
              </p>
              <p className="text-xs text-gray-500">
                All-time cumulative
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="text-2xl font-bold text-purple-600">
                {formatCurrency(stats.lesson_notes_stats?.revenue_this_month || 0)}
              </p>
              <p className="text-xs text-gray-500">
                Monthly performance
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legal Documents Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gavel className="h-5 w-5" />
            Legal Documents Statistics
          </CardTitle>
          <CardDescription>
            Document generation and revenue analytics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Revenue Stats */}
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600">This Month Revenue</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(stats.legal_document_stats?.revenue_this_month || 0)}
                </p>
                <p className="text-xs text-gray-500">
                  {stats.legal_document_stats?.this_month_generated || 0} documents
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600">This Year Revenue</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(stats.legal_document_stats?.revenue_this_year || 0)}
                </p>
                <p className="text-xs text-gray-500">
                  {stats.legal_document_stats?.this_year_generated || 0} documents
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCurrency(stats.legal_document_stats?.revenue_cumulative || 0)}
                </p>
                <p className="text-xs text-gray-500">
                  {stats.legal_document_stats?.total_generated || 0} total documents
                </p>
              </div>
            </div>

            {/* Document Type Breakdown */}
            {stats.legal_document_stats?.document_type_breakdown && 
             stats.legal_document_stats.document_type_breakdown.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Document Type Breakdown</h4>
                <div className="space-y-2">
                  {stats.legal_document_stats.document_type_breakdown.map((type, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">{type.document_type}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <Badge variant="secondary">
                          {type.count} docs
                        </Badge>
                        <span className="text-green-600 font-medium">
                          {formatCurrency(type.revenue)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOverview;